import moment from 'moment';

import { ExecutiveSummaryPreset } from '../config/presets';

const URL_DATE_FORMAT = 'DD-MM-YYYY';

export interface DateRangeStrings {
    startDate: string;
    endDate: string;
}

export interface PresetRanges {
    primary: DateRangeStrings;
    comparison: DateRangeStrings;
}

export interface ComputePresetRangesArgs {
    preset: ExecutiveSummaryPreset;
    /** Project's earliest available data date, `'DD-MM-YYYY'`. */
    boundaryStartDate: string;
    /** Project's latest available data date, `'DD-MM-YYYY'`. */
    boundaryEndDate: string;
}

/**
 * Computes the primary and comparison ranges for a quick-select preset.
 *
 * The primary window is anchored on the latest available data date
 * (`boundaryEndDate`). Every produced date is clamped to
 * `[boundaryStartDate, boundaryEndDate]`, so projects with less than 12/24
 * months of data simply collapse the comparison window toward the lower bound
 * rather than producing dates outside the available data.
 *
 * Adjacent (non year-on-year) windows are made non-overlapping by offsetting
 * the comparison end by one day before the primary start.
 */
export const computePresetRanges = ({
    preset,
    boundaryStartDate,
    boundaryEndDate,
}: ComputePresetRangesArgs): PresetRanges => {
    const lowerBound = moment.utc(boundaryStartDate, URL_DATE_FORMAT);
    const anchor = moment.utc(boundaryEndDate, URL_DATE_FORMAT);

    const clamp = (m: moment.Moment): moment.Moment =>
        moment.max(lowerBound.clone(), moment.min(anchor.clone(), m));

    const fmt = (m: moment.Moment): string => clamp(m).format(URL_DATE_FORMAT);

    const months = preset === 'last12vsPrev12' ? 12 : 6;

    // Primary range: the last `months` months ending on the anchor.
    const primaryEnd = anchor.clone();
    const primaryStart = anchor.clone().subtract(months, 'months').add(1, 'day');

    let comparisonStart: moment.Moment;
    let comparisonEnd: moment.Moment;

    if (preset === 'last6YoY') {
        // Same window, shifted back exactly one year.
        comparisonEnd = primaryEnd.clone().subtract(1, 'year');
        comparisonStart = primaryStart.clone().subtract(1, 'year');
    } else {
        // Immediately preceding window of the same length.
        comparisonEnd = anchor.clone().subtract(months, 'months');
        comparisonStart = comparisonEnd
            .clone()
            .subtract(months, 'months')
            .add(1, 'day');
    }

    return {
        primary: {
            startDate: fmt(primaryStart),
            endDate: fmt(primaryEnd),
        },
        comparison: {
            startDate: fmt(comparisonStart),
            endDate: fmt(comparisonEnd),
        },
    };
};
